import { ActivityLog } from 'types/graphql'

import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { CallStatus, CallType } from 'src/graphql/types/activityLogs'
import { CallConnectedStatus } from 'src/graphql/types/callActivity'
import { isMissedCallStatus } from 'src/lib/activityLogs/callMissedStatus'

interface CallTagsProps {
  activityLog: ActivityLog
}

/**
 * Outcome tags for a call (Missed / Voicemail). The actor marker (AI, Manual)
 * is intentionally NOT part of this group — render CallActorBadge separately
 * (pinned right in the Calls table) so the actor pill always sits in the same
 * spot instead of shifting between Missed and Voicemail.
 *
 * A mailbox is always one amber "Voicemail" chip, never "Missed" as well,
 * and always this same StyledBadge: a recruiting call that left our script
 * carries LEFT_VOICEMAIL; a VoE agent call that hit a mailbox carries the
 * VOICEMAIL disposition (ENG-4756). CallRow skips CallConversionBadge for the
 * latter so the chip is not drawn twice.
 *
 * Width under pressure (ENG-4765 / ENG-4758): in a tight Tags cell the
 * Mantine badge used to collapse to an empty red/amber box with no text.
 * "Missed" and a bare "Voicemail" are fixed text and never shrink.
 * "Voicemail: <template title>" can carry a 100-character title, so that chip
 * keeps a floor wide enough for "Voicemail…", lets Mantine ellipsize the
 * title, and exposes the full label in a tooltip (hover or keyboard focus)
 * and as the badge's accessible name.
 *
 * Rendered as a fragment, not a wrapper div: the chips must be direct flex
 * items of the caller's row so `shrink-0` / `min-w-*` act on the row itself.
 * A wrapper's own min-content is ~0 (Mantine's label is overflow-hidden), so
 * it would shrink under the chips and let the next tag draw over "Missed".
 */
const CallTags = ({ activityLog }: CallTagsProps) => {
  const isVoicemailDisposition =
    activityLog.connectedStatus === CallConnectedStatus.VOICEMAIL
  const isMissedCall =
    isMissedCallStatus(activityLog.callStatus) && !isVoicemailDisposition
  const isLeftVoiceMail =
    activityLog.callStatus === CallStatus.LEFT_VOICEMAIL ||
    isVoicemailDisposition
  const voiceMailTitle = activityLog.voiceMailTemplate?.title
  const voiceMailLabel = voiceMailTitle
    ? `Voicemail: ${voiceMailTitle}`
    : 'Voicemail'

  return (
    <>
      {isMissedCall && (
        <StyledBadge
          textTransform="capitalize"
          color="red"
          wrapperClassName="shrink-0"
        >
          Missed
        </StyledBadge>
      )}
      {isLeftVoiceMail && !voiceMailTitle && (
        <StyledBadge
          textTransform="capitalize"
          color="orange"
          wrapperClassName="shrink-0"
        >
          Voicemail
        </StyledBadge>
      )}
      {isLeftVoiceMail && voiceMailTitle && (
        <StyledBadge
          textTransform="capitalize"
          color="orange"
          // 6rem leaves ~74px of label after padding: "Voicemail" (58px) plus
          // the ellipsis, so the word itself is never cut.
          wrapperClassName="min-w-[6rem]"
          maw="100%"
          tooltipText={voiceMailLabel}
          aria-label={voiceMailLabel}
          tabIndex={0}
        >
          {voiceMailLabel}
        </StyledBadge>
      )}
    </>
  )
}

/**
 * Who placed/logged the call: the shared sparkle "AI" pill for voice-agent
 * calls (replaces the old blue "Virtual" badge) or the "Manual" pill for
 * hand-registered ones — the same ActorBadge the activity-log and SMS/call
 * threads use, so the actor reads identically everywhere.
 */
export const CallActorBadge = ({
  activityLog,
}: {
  activityLog: ActivityLog
}) => {
  if (activityLog.callType === CallType.BLANDAI) {
    return (
      <ActorBadge
        variant="ai"
        label={activityLog.voiceAgent?.name || 'AI voice agent'}
      />
    )
  }
  // Manual "Register Call" logs omit callStatus/callType; automated
  // Twilio/BlandAI calls always set callStatus (e.g. INITIATED) at creation.
  if (activityLog.callStatus === null) {
    return (
      <ActorBadge variant="manual" label="Logged manually by a recruiter" />
    )
  }
  return null
}

export default CallTags
